import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AlertCircle, CheckCircle, Lightbulb, Link2, BookOpen, Volume2, VolumeX, Loader2 } from 'lucide-react';
import { usePhrases } from '@/context/PhrasesContext';
import { useLanguage } from '@/context/LanguageContext';
import { getCategoryColor } from '@/utils/categoryColors';
import { DinoLlamaLoader } from '@/components/DinoLlamaLoader';
import { apiFetch } from '@/hooks/useFetch';

export const PhraseDetail = ({ phrase }) => {
  const navigate = useNavigate();
  const { categories, phrases } = usePhrases();
  const { t, isSpanish, translatePhraseContent, translateSingleCategory } = useLanguage();
  const [translatedPhrase, setTranslatedPhrase] = useState(null);
  const [translatedCategory, setTranslatedCategory] = useState(null);
  const [isTranslatingPhrase, setIsTranslatingPhrase] = useState(false);
  
  // Audio state
  const [hasAudio, setHasAudio] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);
  
  const category = categories.find(c => c.id === phrase.category);
  const colors = getCategoryColor(phrase.category);
  
  // Check if audio is available for this phrase
  useEffect(() => {
    const checkAudio = async () => {
      if (!phrase?.id) return;
      
      try {
        const { data } = await apiFetch(`/api/audio/check/${phrase.id}`);
        if (data) {
          setHasAudio(data.has_audio);
          if (data.has_audio) {
            // Pre-fetch the audio URL
            const { data: audioData } = await apiFetch(`/api/audio/phrase/${phrase.id}`);
            if (audioData) {
              setAudioUrl(audioData.audio_url);
            }
          }
        }
      } catch (error) {
        console.error('Error checking audio:', error);
        setHasAudio(false);
      }
    };
    
    checkAudio();
    
    // Reset audio state when phrase changes
    return () => {
      setIsPlaying(false);
      setAudioUrl(null);
      setHasAudio(false);
    };
  }, [phrase?.id]);
  
  // Handle audio playback
  const handlePlayAudio = async () => {
    if (!audioUrl) return;
    
    if (isPlaying && audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
      return;
    }
    
    setIsLoadingAudio(true);
    
    try {
      if (!audioRef.current) {
        audioRef.current = new Audio(audioUrl);
        audioRef.current.onended = () => setIsPlaying(false);
        audioRef.current.onerror = () => {
          setIsPlaying(false);
          setIsLoadingAudio(false);
          console.error('Audio playback error');
        };
      }
      
      await audioRef.current.play();
      setIsPlaying(true);
    } catch (error) {
      console.error('Error playing audio:', error);
    } finally {
      setIsLoadingAudio(false);
    }
  };
  
  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);
  
  // Translate phrase content when language changes to Spanish
  // NOTE: All phrase content is stored in English in the database, including Spanish category phrases
  useEffect(() => {
    const doTranslate = async () => {
      // Only translate when in Spanish mode
      if (isSpanish && phrase) {
        setIsTranslatingPhrase(true);
        try {
          const translated = await translatePhraseContent(phrase);
          setTranslatedPhrase(translated);
          
          // Also translate category name using the single category function
          if (category) {
            const translatedCat = await translateSingleCategory(category);
            setTranslatedCategory(translatedCat);
          }
        } finally {
          setIsTranslatingPhrase(false);
        }
      } else {
        // In English mode, use original content (no translation needed)
        setTranslatedPhrase(null);
        setTranslatedCategory(null);
      }
    };
    doTranslate();
  }, [phrase, isSpanish, translatePhraseContent, translateSingleCategory, category]);
  
  // Use translated content only in Spanish mode
  const displayPhrase = isSpanish && translatedPhrase ? translatedPhrase : phrase;
  const displayCategory = isSpanish && translatedCategory ? translatedCategory : category;
  
  // IMPORTANT: The phrase itself should NEVER be translated - only the definitions
  // Always use the original phrase name
  const phraseText = phrase.phrase;  // Never translate the phrase itself
  
  // Get definitions (translated if in translation mode)
  const literalMeaning = displayPhrase.literalMeaning;
  const actualMeaning = displayPhrase.actualMeaning;
  const context = displayPhrase.context;
  const examples = displayPhrase.examples;
  const tips = displayPhrase.tips;
  
  // Separate related phrases into two groups:
  // 1. Defined phrases (exist in the app) - clickable links
  // 2. Thesaurus words from the thesaurus field - synonyms/similar words
  const { definedPhrases, thesaurusWords, otherRelated } = useMemo(() => {
    const defined = [];
    const other = [];
    const thesaurus = phrase.thesaurus || []; // Use actual thesaurus field from database
    
    // Process related phrases - separate into clickable (exist in DB) and non-clickable
    if (phrase.relatedPhrases && phrase.relatedPhrases.length > 0) {
      phrase.relatedPhrases.forEach(related => {
        // Check if this phrase exists in the database (exact match, case-insensitive)
        const exists = phrases.some(p => 
          p.phrase.toLowerCase() === related.toLowerCase()
        );
        
        if (exists) {
          defined.push(related);
        } else {
          other.push(related);
        }
      });
    }
    
    return { definedPhrases: defined, thesaurusWords: thesaurus, otherRelated: other };
  }, [phrase.relatedPhrases, phrase.thesaurus, phrases]);
  
  // Navigate to a defined phrase
  const handleRelatedPhraseClick = (relatedPhraseName) => {
    const matchedPhrase = phrases.find(p => 
      p.phrase.toLowerCase() === relatedPhraseName.toLowerCase()
    );
    
    if (matchedPhrase) {
      navigate(`/phrase/${matchedPhrase.id}`);
    }
  };
  
  return (
    <div className="space-y-6">
      {/* Translation indicator with Dino Llama */}
      {isTranslatingPhrase && (
        <DinoLlamaLoader message={t('translating')} size="sm" />
      )}
      
      {/* Main Phrase Card */}
      <Card className="border-2">
        <CardHeader className="space-y-4">
          <div className="flex items-center justify-between">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium w-fit ${colors.bg} ${colors.text}`}>
              {displayCategory?.name || 'General'}
            </span>
            
            {/* Audio Playback Button */}
            {hasAudio && (
              <Button
                variant="outline"
                size="sm"
                onClick={handlePlayAudio}
                disabled={isLoadingAudio}
                className="flex items-center gap-2 border-2 border-[#00E5CC] bg-[#00E5CC]/20 text-[#00E5CC] font-bold hover:bg-[#00E5CC]/30 hover:border-[#00E5CC] shadow-lg transition-all"
                data-testid="audio-play-btn"
              >
                {isLoadingAudio ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : isPlaying ? (
                  <VolumeX className="h-5 w-5" />
                ) : (
                  <Volume2 className="h-5 w-5" />
                )}
                <span>
                  {isPlaying ? (isSpanish ? 'Detener' : 'Stop') : (isSpanish ? 'Escuchar' : 'Listen')}
                </span>
              </Button>
            )}
          </div>
          <CardTitle className="text-3xl sm:text-4xl font-bold leading-tight">
            {phrase.phrase}
          </CardTitle>
          <CardDescription className="text-base">
            {context}
          </CardDescription>
        </CardHeader>
      </Card>
      
      {/* Literal vs Actual Meaning */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card className="border-2 border-destructive/20 bg-destructive/5">
          <CardHeader>
            <div className="flex items-center space-x-2 mb-2">
              <AlertCircle className="h-5 w-5 text-destructive" />
              <CardTitle className="text-lg">{t('literalMeaning')}</CardTitle>
            </div>
            <CardDescription className="text-base leading-relaxed text-foreground/80">
              {literalMeaning}
            </CardDescription>
          </CardHeader>
        </Card>
        
        <Card className="border-2 border-primary/20 bg-primary/5">
          <CardHeader>
            <div className="flex items-center space-x-2 mb-2">
              <CheckCircle className="h-5 w-5 text-primary" />
              <CardTitle className="text-lg">{t('actualMeaning')}</CardTitle>
            </div>
            <CardDescription className="text-base leading-relaxed text-foreground/80">
              {actualMeaning}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
      
      {/* Examples */}
      {examples && examples.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-xl flex items-center space-x-2">
              <MessageCircle className="h-5 w-5 text-primary" />
              <span>{t('examplesInContext')}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {examples.map((example, index) => (
              <div key={index} className="explanation-section">
                <p className="text-sm leading-relaxed">{example}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
      
      {/* Tips */}
      {tips && tips.length > 0 && (
        <Card className="border-accent/30 bg-accent/5">
          <CardHeader>
            <CardTitle className="text-xl flex items-center space-x-2">
              <Lightbulb className="h-5 w-5 text-accent-foreground" />
              <span>{t('helpfulTips')}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3">
              {tips.map((tip, index) => (
                <li key={index} className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-accent text-accent-foreground flex items-center justify-center text-xs font-semibold mt-0.5">
                    {index + 1}
                  </span>
                  <span className="text-sm leading-relaxed flex-1">{tip}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
      
      {/* Phrase Thesaurus - Synonyms not defined in app */}
      {thesaurusWords.length > 0 && (
        <Card className="border-secondary/30 bg-secondary/5">
          <CardHeader>
            <CardTitle className="text-xl flex items-center space-x-2">
              <BookOpen className="h-5 w-5 text-secondary-foreground" />
              <span>{t('phraseThesaurus')}</span>
            </CardTitle>
            <CardDescription className="text-sm">
              {t('similarWords')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-1.5">
              {thesaurusWords.map((word, index) => (
                <Badge 
                  key={index} 
                  variant="secondary"
                  className="text-xs px-2 py-0.5"
                >
                  {word}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Related Phrases - Only phrases defined in app */}
      {(definedPhrases.length > 0 || otherRelated.length > 0) && (
        <Card>
          <CardHeader>
            <CardTitle className="text-xl flex items-center space-x-2">
              <Link2 className="h-5 w-5 text-secondary-foreground" />
              <span>{t('relatedPhrases')}</span>
            </CardTitle>
            {definedPhrases.length > 0 && (
              <CardDescription className="text-sm">
                {t('clickToExplore')}
              </CardDescription>
            )}
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-1.5">
              {/* Clickable phrases that exist in the app */}
              {definedPhrases.map((related, index) => (
                <Badge 
                  key={`defined-${index}`} 
                  variant="secondary" 
                  className="text-xs px-2 py-0.5 cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors border border-border"
                  onClick={() => handleRelatedPhraseClick(related)}
                >
                  {related}
                </Badge>
              ))}
              {/* Non-clickable related phrases (not in app database) */}
              {otherRelated.map((related, index) => (
                <Badge 
                  key={`other-${index}`} 
                  variant="secondary" 
                  className="text-xs px-2 py-0.5"
                >
                  {related}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const MessageCircle = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);
